import { calculateRawAudioFrameBufferSize, calculateRawVideoFrameBufferSize, calculateSampleCountForFrameNumber, calculateSampleOffsetForFrameNumber } from '@mimir/common/transcoding/rawFrame';
import type { AudioFrameV2, AudioInfoV2, VideoInfoV2, FrameInfoV2, FrameV2, VideoFrameV2 } from './frameProvider';
import { invariant } from '@mimir/common/util/invariant';
import type { BufferCache } from './refCountedBuffer';

export function closeVideoFrame(videoFrame: VideoFrameV2 | null): void {
  videoFrame?.buffer.close();
}
export function closeFrame(frame: FrameV2 | null): void {
  closeVideoFrame(frame?.video ?? null);
  frame?.audio?.buffer.close();
}

export function addVideoFrameReference(videoFrame: VideoFrameV2): VideoFrameV2 {
  return {
    resolution: videoFrame.resolution,
    pixelFormat: videoFrame.pixelFormat,
    fieldOrder: videoFrame.fieldOrder,
    buffer: videoFrame.buffer.addReference(),
  };
}

export function addAudioFrameReference(audioFrame: AudioFrameV2): AudioFrameV2 {
  return {
    sampleOffset: audioFrame.sampleOffset,
    sampleCount: audioFrame.sampleCount,
    sampleRate: audioFrame.sampleRate,
    channelCount: audioFrame.channelCount,
    format: audioFrame.format,
    buffer: audioFrame.buffer.addReference(),
  };
}

export function addFrameReference(frame: FrameV2): FrameV2 {
  return {
    frameNumber: frame.frameNumber,
    frameRate: frame.frameRate,
    video: frame.video ? addVideoFrameReference(frame.video) : null,
    audio: frame.audio ? addAudioFrameReference(frame.audio) : null,
  };
}

export function videoInfoFromVideoFrame(videoFrame: VideoFrameV2): VideoInfoV2 {
  return {
    resolution: videoFrame.resolution,
    pixelFormat: videoFrame.pixelFormat,
    fieldOrder: videoFrame.fieldOrder,
  };
}

export function audioInfoFromAudioFrame(audioFrame: AudioFrameV2): AudioInfoV2 {
  return {
    sampleRate: audioFrame.sampleRate,
    format: audioFrame.format,
    channelCount: audioFrame.channelCount,
  };
}

export function infoFromFrame(frame: FrameV2): FrameInfoV2 {
  const info : FrameInfoV2 = {
    frameRate: frame.frameRate,
    video: null,
    audio: null,
  };

  if (frame.video !== null) {
    info.video = videoInfoFromVideoFrame(frame.video);
  }

  if (frame.audio !== null) {
    info.audio = audioInfoFromAudioFrame(frame.audio);
  }
  return info;
}

export function sanitizeFrameInfo(info: FrameInfoV2): FrameInfoV2 {
  return {
    frameRate: info.frameRate,
    video: info.video ? {
      fieldOrder: info.video.fieldOrder,
      resolution: info.video.resolution,
      pixelFormat: info.video.pixelFormat,
    } : null,
    audio: info.audio ? {
      sampleRate: info.audio.sampleRate,
      channelCount: info.audio.channelCount,
      format: info.audio.format,
    } : null,
  };
}

export function makeBlackFrame(info: FrameInfoV2, frameNumber: number, bufferCache: BufferCache): FrameV2 {
  return makeColorFrame(info, frameNumber, bufferCache, { r: 0.0, g: 0.0, b: 0.0, a: 1.0 });
}

export function makeColorFrame(
  info: FrameInfoV2,
  frameNumber: number,
  bufferCache: BufferCache,
  color: {
    r: number,
    g: number,
    b: number,
    a: number
  }
): FrameV2 {
  const frame : FrameV2 = {
    frameRate: info.frameRate,
    frameNumber,
    video: null,
    audio: null,
  };

  if (info.video !== null) {
    frame.video = makeColorVideoFrame(info.video, bufferCache, color);
  }
  if (info.audio !== null) {
    const sampleOffset = calculateSampleOffsetForFrameNumber(info.audio.sampleRate, info.frameRate, frameNumber);
    const sampleCount = calculateSampleCountForFrameNumber(info.audio.sampleRate, info.frameRate, frameNumber);
    const bufferSize = calculateRawAudioFrameBufferSize(info.audio, sampleCount);
    frame.audio = {
      ...info.audio,
      sampleOffset,
      sampleCount,
      buffer: bufferCache.allocateBuffer(bufferSize, (buffer: Uint8Array) => { buffer.fill(0); })
    };
  }

  validateFrame(frame);

  return frame;
}

export function makeBlackVideoFrame(videoInfo: VideoInfoV2, bufferCache: BufferCache): VideoFrameV2 {
  return makeColorVideoFrame(videoInfo, bufferCache, { r: 0.0, g: 0.0, b: 0.0, a: 1.0 });
}

export function makeColorVideoFrame(
  videoInfo: VideoInfoV2,
  bufferCache: BufferCache,
  color: {
    r: number,
    g: number,
    b: number,
    a: number
  }
): VideoFrameV2 {
  const { width, height } = videoInfo.resolution;
  const bufferSize = calculateRawVideoFrameBufferSize(width, height, 'rgba');

  const colorUint32 = (
    // eslint-disable-next-line no-bitwise
    (normalizedColorComponentTo8BitValue(color.a) << 24) +
    // eslint-disable-next-line no-bitwise
    (normalizedColorComponentTo8BitValue(color.b) << 16) +
    // eslint-disable-next-line no-bitwise
    (normalizedColorComponentTo8BitValue(color.g) << 8) +
    normalizedColorComponentTo8BitValue(color.r)
  );
  const video: VideoFrameV2 = {
    resolution: videoInfo.resolution,
    fieldOrder: videoInfo.fieldOrder,
    pixelFormat: 'rgba',
    buffer: bufferCache.allocateBuffer(bufferSize, (buffer: Uint8Array) => {
      const uint32Array = new Uint32Array(buffer.buffer);
      uint32Array.fill(colorUint32);
    }),
  };
  return video;
}

function normalizedColorComponentTo8BitValue(n: number) {
  return Math.min(Math.max(0, Math.round(n * 0xFF)), 0xFF);
}

export function makeSilentAudioFrame(
  audioInfo: AudioInfoV2,
  sampleOffset: number,
  sampleCount: number,
  bufferCache: BufferCache
): AudioFrameV2 {
  const bufferSize = calculateRawAudioFrameBufferSize(audioInfo, sampleCount);
  return {
    ...audioInfo,
    sampleOffset,
    sampleCount,
    buffer: bufferCache.allocateBuffer(
      bufferSize,
      (buffer: Uint8Array) => {
        buffer.fill(0);
      }
    )
  };
}

export function validateAudioFrame(audioFrame: AudioFrameV2) {
  const expectedBufferSize = calculateRawAudioFrameBufferSize(audioFrame, audioFrame.sampleCount);
  invariant(audioFrame.buffer.size() === expectedBufferSize, `Frame validation failed. Expected audioBufferSize to be ${expectedBufferSize} but it is ${audioFrame.buffer.size()}`);
}

export function validateFrame(frame: FrameV2) {
  if (frame.audio !== null) {
    const expectedSampleCount = calculateSampleCountForFrameNumber(frame.audio.sampleRate, frame.frameRate, frame.frameNumber);
    const expectedSampleOffset = calculateSampleOffsetForFrameNumber(frame.audio.sampleRate, frame.frameRate, frame.frameNumber);
    invariant(frame.audio.sampleCount === expectedSampleCount, `Frame validation failed. Expected sampleCount to be ${expectedSampleCount} for frame number ${frame.frameNumber}, but it is ${frame.audio.sampleCount}`);
    invariant(frame.audio.sampleOffset === expectedSampleOffset, `Frame validation failed. Expected sampleOffset to be ${expectedSampleOffset} for frame number ${frame.frameNumber}, but it is ${frame.audio.sampleOffset}`);
    validateAudioFrame(frame.audio);
  }
  invariant(frame.frameRate.numerator !== 0, 'Frame rate is zero.');
  invariant(frame.frameRate.numerator > 0, 'Frame rate numerator is negative.');
  invariant(frame.frameRate.denominator !== 0, 'Frame rate numerator is zero.');
  invariant(frame.frameRate.denominator > 0, 'Frame rate numerator is negative.');
}
